import React from "react";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import {
  capitalizeFirstLetter,
  loadImageDimensions,
  parseAmount,
} from "../modules/invoiceUtils";
import uRLs from "../../utils/baseUtils";
import { usePermission } from "../../context/PermissionContext";

export const generateInvReturnInvoicePdf = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,
) => {
  const { documentConfig, isInterState, isPreSale, timezone } = invoiceConfig;
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const padding = 5;
  const headerX = 2;
  const headerY = 2;
  const imagePath =  documentConfig?.image;

  if (documentConfig?.image) {
    try {
      // Convert image URL to base64
      const response = await fetch(imagePath);
      const blob = await response.blob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Load original dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Resize logic
      const maxWidth = 50;
      const maxHeight = 10;
      let imgWidth = naturalWidth;
      let imgHeight = naturalHeight;
      const widthRatio = maxWidth / imgWidth;
      const heightRatio = maxHeight / imgHeight;
      const ratio = Math.min(widthRatio, heightRatio);
      imgWidth *= ratio;
      imgHeight *= ratio;

      // Determine format
      const getImageFormat = (path) => {
        const ext = path.split(".").pop().toLowerCase();
        if (ext === "jpg" || ext === "jpeg") return "JPEG";
        if (ext === "png") return "PNG";
        return "JPEG";
      };
      const format = getImageFormat(imagePath);

      // Add image to PDF
      doc.addImage(
        base64,
        format,
        headerX + padding,
        headerY + padding,
        imgWidth,
        imgHeight,
      );
    } catch (err) {
      console.warn("Image failed to load:", err);
    }
  }

  const invoiceFontSize = 20;
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(invoiceFontSize);
  // Estimate vertical alignment by adjusting for font size
  const textHeightOffset = invoiceFontSize * 0.55;
  const invoiceTextY = headerY + textHeightOffset;
  const invoiceX = pageWidth - padding;
  const invoiceText = documentConfig?.title && documentConfig?.title;
  const invoiceTextWidth = doc.getTextWidth(invoiceText);
  doc.text(invoiceText, invoiceX, invoiceTextY, { align: "right" });
  // If cancelled, show "CANCELLED" immediately to the right of "INVOICE"
  if (data?.status === "CANCELLED") {
    const cancelledFontSize = invoiceFontSize * 0.6;
    doc.setFontSize(cancelledFontSize);
    doc.setTextColor(220, 53, 69); // Bootstrap danger red
    doc.setFont("Inter-Bold", "bold");
    // Position CANCELLED right after INVOICE
    const cancelledX = invoiceX - invoiceTextWidth - 8; // 8 is spacing between words
    doc.text("CANCELLED", cancelledX, invoiceTextY, { align: "right" });
    // Reset styles
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(invoiceFontSize);
  }
  // Prepare font size for labels and values
  doc.setFontSize(10);
  const invoiceNoLabel = documentConfig?.numberLabel;
  const invoiceNoValue = documentConfig?.number;
  const invoiceDateLabel = "Date:";
  const invoiceDateValue = documentConfig?.Date.date;
  // Measure total widths
  const invoiceNoLabelWidth = doc.getTextWidth(invoiceNoLabel);
  const invoiceNoValueWidth = doc.getTextWidth(invoiceNoValue);
  const invoiceDateLabelWidth = doc.getTextWidth(invoiceDateLabel);
  const invoiceDateValueWidth = doc.getTextWidth(invoiceDateValue);
  const gap = 10;
  const innerGap = 2;
  const invoiceRowY = 25;
  const totalTextWidth =
    invoiceNoLabelWidth +
    innerGap +
    invoiceNoValueWidth +
    gap +
    invoiceDateLabelWidth +
    innerGap +
    invoiceDateValueWidth;
  const invoiceRowX = pageWidth - padding - totalTextWidth;
  const lineStartX = padding;
  const lineEndX = invoiceRowX - 5;
  const lineY = invoiceRowY;
  // Middle line
  doc.setDrawColor(selectedTheme?.templateTheme?.primaryColor);
  doc.setLineWidth(2);
  doc.setLineCap("round");
  doc.line(lineStartX, lineY, lineEndX, lineY);

  doc.setFont("Inter-Bold", "bold");
  doc.text(invoiceNoLabel, invoiceRowX, invoiceRowY);
  doc.setFont("Inter-Regular", "normal");
  doc.text(
    invoiceNoValue,
    invoiceRowX + invoiceNoLabelWidth + innerGap,
    invoiceRowY,
  );
  const dateX =
    invoiceRowX + invoiceNoLabelWidth + innerGap + invoiceNoValueWidth + gap;

  doc.setFont("Inter-Bold", "bold");
  doc.text(invoiceDateLabel, dateX, invoiceRowY);

  doc.setFont("Inter-Regular", "normal");
  doc.text(
    invoiceDateValue,
    dateX + invoiceDateLabelWidth + innerGap,
    invoiceRowY,
  );
  const sectionTop = 30;
  const col2X = padding;
  doc.setFontSize(8);
  const hasSaleToData =
    documentConfig?.toData &&
    (documentConfig?.toData?.name ||
      documentConfig?.toData?.addressline1 ||
      documentConfig?.toData?.addressline2 ||
      documentConfig?.toData?.district ||    
      documentConfig?.toData?.pincode ||
      documentConfig?.toData?.phone ||
      documentConfig?.toData?.email ||
      documentConfig?.toData?.gstnumber ||
      documentConfig?.toData?.state ||
      documentConfig?.toData?.statecode ||
      documentConfig?.toData?.referenceid);

  const summaryWidth = 70;
  const summaryX = pageWidth - padding - summaryWidth;
  let summaryY = sectionTop;

  // FIX 1: Dynamic calculation to give customer details max available width before hitting the box
  const sectionWidth = summaryX - col2X - 8;

  // FIX 2: Adjusted Customer title to align perfectly with the "Summary" internal text row
  if (hasSaleToData) {
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.text(documentConfig?.toLabel, col2X, sectionTop + 4);
  }

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(8);

  // FIX 3: Set initial starting coordinates for details to match box layout flow
  let fromY = sectionTop + 10;
  let toY = sectionTop + 10;

  const sectionGap = 12;

  let toDetails = [];
  if (hasSaleToData) {
   if (hasSaleToData) {
  toDetails = [
    {
      bold: true,
      text: documentConfig?.toData?.name || "-",
    },

    {
      bold: false,
      text: [
        documentConfig?.toData?.addressline1,
        documentConfig?.toData?.addressline2,
        documentConfig?.toData?.district,
        documentConfig?.toData?.country,
        documentConfig?.toData?.pincode,
      ]
        .filter(Boolean)
        .join(", "),
    },

    ...(documentConfig?.toData?.phone
      ? [
          {
            bold: false,
            text: `Phone: ${documentConfig?.toData?.phone}`,
          },
        ]
      : []),

    ...(documentConfig?.toData?.email
      ? [
          {
            bold: false,
            text: `Email: ${documentConfig?.toData?.email}`,
          },
        ]
      : []),

    ...(documentConfig?.toData?.referenceid
      ? [
          {
            bold: false,
            text: "Reference No:",
            suffix: ` ${documentConfig?.toData?.referenceid}`,
          },
        ]
      : []),

    ...(documentConfig?.toData?.gstnumber
      ? [
          {
            bold: true,
            text: "GST:",
            suffix: ` ${documentConfig?.toData?.gstnumber}`,
          },
        ]
      : []),

    ...(documentConfig?.toData?.fssai
      ? [
          {
            bold: true,
            text: "FSSAI:",
            suffix: ` ${documentConfig?.toData?.fssai}`,
          },
        ]
      : []),

    ...(documentConfig?.toData?.cinnumber
      ? [
          {
            bold: true,
            text: "CIN:",
            suffix: ` ${documentConfig?.toData?.cinnumber}`,
          },
        ]
      : []),

    ...(documentConfig?.toData?.pannumber
      ? [
          {
            bold: true,
            text: "PAN:",
            suffix: ` ${documentConfig?.toData?.pannumber}`,
          },
        ]
      : []),

    ...(documentConfig?.toData?.state
      ? [
          {
            bold: true,
            text: "State:",
            suffix: ` ${documentConfig?.toData?.state} [ State Code: ${
              documentConfig?.toData?.statecode || ""
            } ]`,
          },
        ]
      : []),
  ];
}
  }
  const wrapText = (text, maxWidth) => doc.splitTextToSize(text, maxWidth);
  const lineHeight = 4.5; // Slightly increased for matching summary grid line heights

  const renderDetails = (details, startX, startY) => {
    let y = startY;
    details.forEach(({ bold, text, suffix = "" }) => {
      const fullText = text + suffix;
      const lines = wrapText(fullText, sectionWidth);
      lines.forEach((line, index) => {
        if (index === 0 && suffix && bold) {
          const labelWidth = doc.getTextWidth(text);
          doc.setFont("Inter-Bold", "bold");
          doc.text(text, startX, y);
          doc.setFont("Inter-Regular", "normal");
          doc.text(suffix, startX + labelWidth, y);
        } else {
          doc.setFont(
            bold ? "Inter-Bold" : "Inter-Regular",
            bold ? "bold" : "normal",
          );
          doc.text(line, startX, y);
        }
        y += lineHeight;
      });
    });

    return y;
  };

  let shippingY = sectionTop + 5;
  if (hasSaleToData) {
    toY = renderDetails(toDetails, col2X, toY);
  }

  const summaryHeight = 25;
  doc.setLineWidth(0.1);
  doc.setDrawColor(selectedTheme?.templateTheme?.primaryColor);
  const themeColor = selectedTheme?.templateTheme?.primaryColor || "#F0F5FF";
  doc.setFillColor(themeColor);
  doc.roundedRect(summaryX, summaryY, summaryWidth, summaryHeight, 2, 2, "FD");

  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(9);
  doc.setTextColor(50, 50, 50);
  doc.text(" Original Invoice Summary", summaryX + 4, summaryY + 5);

  doc.setLineWidth(0.15);
  doc.line(
    summaryX + 4,
    summaryY + 7.5,
    summaryX + summaryWidth - 4,
    summaryY + 7.5,
  );

  const summaryRows = [
    { label: "Sale Mode", value: documentConfig?.saleMode || "INVOICE" },
    { label: "Sale No", value: documentConfig?.saleNo || "-" },
    {
      label: "Sale Date",
      value:
        DateConverter.ISOToDisplayFormat(
          DateConverter.convertToTimeZone(
            documentConfig?.originalSaleDate,
            timezone,
          ),
        ).date || "-",
    },
  ];

  let rowY = summaryY + 12.5;
  const rowLineHeight = 4.5;

  summaryRows.forEach((row) => {
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(row.label, summaryX + 4, rowY);

    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0, 0, 0);

    const valWidth = doc.getTextWidth(row.value);
    doc.text(row.value, summaryX + summaryWidth - 4 - valWidth, rowY);

    rowY += rowLineHeight;
  });

  shippingY = summaryY + summaryHeight + 6;
  const maxSectionY = Math.max(fromY, toY, shippingY);

  // 3. Product Table
  const productBody = documentConfig?.returnItems?.map((item, i) => {
    const qty = item?.quantity || 1;
    const landingCost = item?.lineTotalLandingCost;
    const rate = item?.returnPrice;
    // const gross = item?.lineTotalSoldPrice;
    const taxable = item?.lineTotalBasePrice;

    const gstPercentage = item?.taxPercentage;
    const gstAmt = item?.lineTotalTaxAmount;

    const total = item?.lineGrandTotal;

    const desc = [
      `${item.title}`,
      ...(item?.itemRemarks ? [`"Remark" : ${item.itemRemarks}`] : []),
      ...(item?.itemCondition ? [`"Condition" : ${item.itemCondition}`] : []),
      ...(item?.imode === "PRODUCT" && item?.note ? [item?.note] : []),
      ...(item?.imode === "SERVICE" && item?.description
        ? [item?.description]
        : []),
      ...(item?.hsnsac
        ? [`${item?.hsnsac === "SERVICE" ? "SAC" : "HSN"} : ${item.hsnsac}`]
        : []),
    ].join("\n");
    return [
      i + 1,
      desc,
      `${rate}`,
      `${qty} ${(() => {
        const unitCode = item?.isSecondaryUnit
          ? item?.unitConfig?.secondaryUnitCode
          : item?.unitConfig?.primaryUnitCode;

        // return unitCode !== "unit" ? unitCode : "";
        return unitCode || "";
      })()}`,
      // `${gross}`,
      `${taxable}`,
      `${gstPercentage}%`,
      `${gstAmt}`,
      `${total}`,
    ];
  });

  const soldTotal = documentConfig?.totalsoldprice;

  const baseTotal = documentConfig?.totalBasePrice;

  const taxTotal = documentConfig?.totalTaxAmount;

  const grandTotal = documentConfig?.returnGrandTotalRounded;

  // // Add total row INSIDE the same table
  productBody.push([
    {
      content: "",
      colSpan: 2,
      styles: { fontStyle: "bold", font: "Inter-Bold" },
    },
    {
      content: "Total",
      styles: { halign: "right", fontStyle: "bold", font: "Inter-Bold" },
    },
    {
      content: "",
      styles: { fontStyle: "bold", font: "Inter-Bold" },
    },
    {
      content: `${baseTotal}`,
      styles: { fontStyle: "bold", font: "Inter-Bold" },
    },
    {
      content: ``,
      styles: { fontStyle: "bold", font: "Inter-Bold" },
    },
    {
      content: `${taxTotal}`,
      styles: { fontStyle: "bold", font: "Inter-Bold" },
    },
    {
      content: `${grandTotal}`,
      styles: { fontStyle: "bold", font: "Inter-Bold" },
    },
  ]);
  autoTable(doc, {
    startY: maxSectionY,
    head: [
      [
        "S.No",
        "Description",
        " Rate",
        "Qty",
        // "Gross Amount",
        "Tax Val",
        "GST(%)",
        "GST Amount",
        "Total Return",
      ],
    ],
    body: productBody,
    margin: padding, // add right margin if needed
    tableWidth: doc.internal.pageSize.getWidth() - 10,
    styles: {
      font: "Inter-Regular",
      fontStyle: "normal",
      fontSize: 8,
      halign: "middle",
      cellPadding: 1,
      lineColor: selectedTheme?.templateTheme?.borderColor,
      lineWidth: 0.2,
      textColor: "#041121",
    },
    headStyles: {
      fillColor: selectedTheme?.templateTheme?.primaryColor,
      textColor: selectedTheme.templateTheme?.foregroundColor,
      fontStyle: "bold",
      font: "Inter-Bold",
      halign: "center",
      valign: "middle",
    },
    columnStyles: {
      0: { cellWidth: 13, halign: "center", valign: "middle" },
      1: {
        cellWidth: "auto",
        halign: "left",
        styles: {
          overflow: "linebreak",
        },
      },
      2: { cellWidth: 25, halign: "right" },
      3: { cellWidth: 13, halign: "center" },
      4: { cellWidth: 25, halign: "right" },
      5: { cellWidth: 18, halign: "center" },
      6: { cellWidth: 25, halign: "right" },
      7: { cellWidth: 25, halign: "right" },
    },

    theme: "grid",
  });

  const totalBase = documentConfig?.totalBasePrice || 0;

  const igst = documentConfig?.igstAmount || 0;

  const sgst = documentConfig?.sgstAmount || 0;

  const cgst = documentConfig?.cgstAmount || 0;

  const totalSold = documentConfig?.totalReturnAmount;

  const body = [
    [
      {
        content: "Total Amount In Words",
        styles: {
          fontStyle: "bold",
          font: "Inter-Bold",
          fontSize: 8,
          valign: "top",
        },
      },
      {
        content: "Total Amount Before Tax",
        styles: { halign: "right", fontSize: 8 },
      },
      {
        content: `${totalBase}`,
        styles: { halign: "right", valign: "middle", fontSize: 8 },
      },
    ],
    [
      {
        content: documentConfig?.amountInWords,
        rowSpan: isInterState ? 3 : 4,
        styles: {
          halign: "center",
          valign: "middle",
          fontSize: 8,
        },
      },
      {
        content: isInterState ? "IGST" : "SGST",
        styles: { halign: "right", fontSize: 8 },
      },
      {
        content: isInterState ? `${igst}` : `${sgst}`,
        styles: { halign: "right", valign: "middle", fontSize: 8 },
      },
    ],
  ];
  if (!isInterState) {
    body.push([
      { content: "CGST", styles: { halign: "right", fontSize: 8 } },
      {
        content: `${cgst}`,
        styles: { halign: "right", valign: "middle", fontSize: 8 },
      },
    ]);
  }
  // Add Total row
  body.push([
    { content: "Total", styles: { halign: "right", fontSize: 8 } },
    {
      content: `${totalSold}`,
      styles: { halign: "right", valign: "middle", fontSize: 8 },
    },
  ]);
  // Add Grand Total row
  body.push([
    {
      content: "Grand Total (Rounded)",
      styles: { fontStyle: "bold", halign: "right", fontSize: 8 },
    },
    {
      content: `${documentConfig?.grandTotal}`,
      styles: {
        halign: "right",
        valign: "middle",
        fontStyle: "bold",
        fontSize: 7.5,
      },
    },
  ]);
  const afterProductsY = doc.lastAutoTable?.finalY + 0.3;
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginBottom = 20;
  const estimatedSummaryHeight = 50;
  let summaryStartY;
  let isSamePage = true;
  if (afterProductsY + estimatedSummaryHeight + marginBottom <= pageHeight) {
    summaryStartY = afterProductsY;
  } else {
    doc.addPage();
    summaryStartY = 20;
    isSamePage = false;
  }
  autoTable(doc, {
    startY: summaryStartY,
    body: body,
    pageBreak: "avoid",
    margin:padding,
    tableWidth: doc.internal.pageSize.getWidth() - 10,
    styles: {
      font: "Inter-Regular",
      fontStyle: "normal",
      halign: "middle",
      cellPadding: 2,
      fontSize: 8,
      lineColor: selectedTheme?.templateTheme?.borderColor,
      lineWidth: 0.2,
      fillColor: selectedTheme?.templateTheme?.primaryColor,
      textColor: selectedTheme.templateTheme?.foregroundColor,
    },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 81, halign: "right" },
      2: { cellWidth: 25, halign: "center", valign: "middle" },
    },
    didParseCell: function (data) {
      if (data.row.index === 0 && isSamePage) {
        data.cell.styles.lineWidth = {
          top: 0,
          right: 0.2,
          bottom: 0.2,
          left: 0.2,
        };
      }
      const lastRowIndex = body.length - 1;
      if (data.section === "body") {
        if (
          data.row.index === lastRowIndex &&
          (data.column.index === 1 || data.column.index === 2)
        ) {
          // data.cell.styles.fillColor = "#f4f4f6";
          // data.cell.styles.textColor = [0, 0, 0];
          data.cell.styles.font = "Inter-Bold";
          data.cell.styles.fontStyle = "bold";
        } else {
          data.cell.styles.textColor = [0, 0, 0];
          data.cell.styles.fillColor = "#ffffff";
          data.cell.styles.font = "Inter-Regular";
          data.cell.styles.fontStyle = "normal";
        }
      }
    },
    theme: "grid",
  });
  const afterSummaryY = doc.lastAutoTable.finalY;
  const pageHeight2 = doc.internal.pageSize.getHeight();
  const marginBottom2 = 20;
  const declarationHeight = 45;
  let finalY;
  if (afterSummaryY + declarationHeight + marginBottom2 > pageHeight2) {
    doc.addPage();
    finalY = 10;
  } else {
    finalY = afterSummaryY;
  }
  let currentY = finalY + 8;
  const totalDiscount = documentConfig.totalDiscount;
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.text(
      `Your Total Discount : ${documentConfig?.totalDiscount}`,
      105,
      currentY,
      {
        align: "center",
      },
    );

    currentY += 9; // ONLY if discount exists
  }

  // // ---------------- PAYMENT MODE TABLE (LEFT – 2 INCH STYLE) ----------------

  const payacsh = CurrencyConverter.toCurrency(
    documentConfig?.payDetails?.CASH || 0,
    "INR",
  );
  const payacard = CurrencyConverter.toCurrency(
    documentConfig?.payDetails?.CARD || 0,
    "INR",
  );
  const payaonline = CurrencyConverter.toCurrency(
    documentConfig?.payDetails?.ONLINE || 0,
    "INR",
  );
  const payUpi = CurrencyConverter.toCurrency(
    documentConfig?.payDetails?.UPI || 0,
    "INR",
  );
  const payPaylater = CurrencyConverter.toCurrency(
    documentConfig?.payDetails?.PAYLATER || 0,
    "INR",
  );
  const paymentRows = [];
  // // condition based rows
  if (documentConfig?.payDetails?.CASH > 0) {
    paymentRows.push(["Cash", `${payacsh}`]);
  }

  if (documentConfig?.payDetails?.CARD > 0) {
    paymentRows.push(["Card", `${payacard}`]);
  }

  if (documentConfig?.payDetails?.ONLINE > 0) {
    paymentRows.push(["Online Amount", `${payaonline}`]);
  }

  if (documentConfig?.payDetails?.UPI > 0) {
    paymentRows.push(["UPI", `${payUpi}`]);
  }

  if (documentConfig?.payDetails?.BALANCE > 0) {
    paymentRows.push(["Balance Due", `${payPaylater}`]);
  }

  // // draw table ONLY if rows exist
  if (paymentRows.length > 0) {
    autoTable(doc, {
      startY: currentY,
      head: [["Refund Method", ""]],
      body: paymentRows,
      margin: { left: 6 },
      tableWidth: 70,
      styles: {
        font: "Inter-Regular",
        fontSize: 8,
        cellPadding: 2,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        lineWidth: 0.2,
        textColor: "#000",
      },
      headStyles: {
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme?.templateTheme?.foregroundColor,
        font: "Inter-Bold",
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 40, halign: "left" },
        1: { cellWidth: 30, halign: "right" },
      },
      theme: "grid",
    });

    currentY = doc.lastAutoTable.finalY + 6;
  }
  const innerPaddingX = 2;
  // ===================== Remark =====================
  if (documentConfig?.remarks) {
    // Page break check
    if (currentY > pageHeight - 35) {
      doc.addPage();
      currentY = padding + 5;
    }

    const remarkX = padding;
    const innerPaddingX = 2;
    const remarkWidth = pageWidth - padding * 2 - innerPaddingX * 2 + 5;

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.setTextColor(72, 72, 72);
    doc.text("Remark", remarkX, currentY);

    const remarkLines = doc.splitTextToSize(
      capitalizeFirstLetter(documentConfig.remarks),
      remarkWidth + 30,
    );

    const lineHeight = 4;
    const paddingTop = 2.5;
    const paddingBottom = 2.5;

    const remarkHeight =
      paddingTop + remarkLines.length * lineHeight + paddingBottom;

    const boxY = currentY + 4;

    // Border
    doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
    doc.setLineWidth(0.2);
    // Remark Text
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);
    doc.setTextColor(0);

    let textY = boxY + paddingTop - 1;

    remarkLines.forEach((line) => {
      doc.text(line, remarkX, textY);
      textY += lineHeight;
    });

    // Move below remark
    currentY = boxY + remarkHeight + 4;
  }
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(10);
  doc.text("***** Thank You! Visit Us Again *****", pageWidth / 2, currentY, {
    align: "center",
  });
  if (storeSettings?.negobillBranding) {
    currentY += 5;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.text("Powerd By", pageWidth / 2, currentY, { align: "center" });
    currentY += 5;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.text("www.NegoBill.com", pageWidth / 2, currentY, {
      align: "center",
    });
  }
  currentY += 8;

  const rightX = pageWidth - padding;

  const boxTopY = currentY + 3;

  const SIGN_Y = boxTopY; // Signature image
  const LABEL_Y = SIGN_Y + 8; // "Issued By, Signature"
  const STORE_Y = LABEL_Y + 4; // Store name

  // // Fonts
  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(8);

  // // -------- 1️⃣ Signature Image FIRST ----------
  const signimg = uRLs.IMAGE_URL + storeSettings?.invoiceSettings?.signature;

  if (storeSettings?.invoiceSettings?.signature) {
    try {
      const response = await fetch(signimg);
      const blob = await response.blob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const { width, height } = await loadImageDimensions(base64);

      const maxWidth = 50;
      const maxHeight = 10;

      const ratio = Math.min(maxWidth / width, maxHeight / height);
      const imgWidth = width * ratio;
      const imgHeight = height * ratio;

      const format = signimg.endsWith(".png") ? "PNG" : "JPEG";
      const imgX = rightX - imgWidth;

      doc.addImage(base64, format, imgX, SIGN_Y, imgWidth, imgHeight);
    } catch (err) {
      console.warn("Image failed to load:", err);
    }
  }

  // // -------- 2️⃣ Issued By, Signature ----------
  doc.text("Issued By, Signature", rightX, LABEL_Y + 4, { align: "right" });

  // // -------- 3️⃣ Store Name ----------
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(7);

  const titleText = String(
    documentConfig?.billingStoreProfileData?.title || "",
  );
  doc.text(titleText, rightX, STORE_Y + 4, { align: "right" });

  // setDownload(false);
  // setPrint(false);
  return doc;
};

export const generateInvReturn_HA4_4PDF = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,
) => {
  const {
    documentConfig,   
    timezone,    
  } = invoiceConfig;
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const HALF_PAGE_HEIGHT = 140;
  const padding = 5;
  const headerX = 2;
  const headerY = 2;
  const imagePath =  documentConfig?.image;

  if (documentConfig?.image) {
    try {
      // Convert image URL to base64
      const response = await fetch(imagePath);
      const blob = await response.blob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Load original dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Resize logic
      const maxWidth = 50;
      const maxHeight = 10;
      let imgWidth = naturalWidth;
      let imgHeight = naturalHeight;
      const widthRatio = maxWidth / imgWidth;
      const heightRatio = maxHeight / imgHeight;
      const ratio = Math.min(widthRatio, heightRatio);
      imgWidth *= ratio;
      imgHeight *= ratio;

      // Determine format
      const getImageFormat = (path) => {
        const ext = path.split(".").pop().toLowerCase();
        if (ext === "jpg" || ext === "jpeg") return "JPEG";
        if (ext === "png") return "PNG";
        return "JPEG";
      };
      const format = getImageFormat(imagePath);

      // Add image to PDF
      doc.addImage(
        base64,
        format,
        headerX + padding,
        headerY + padding,
        imgWidth,
        imgHeight,
      );
    } catch (err) {
      console.warn("Image failed to load:", err);
    }
  }

  const invoiceFontSize = 20;
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(invoiceFontSize);
  // Estimate vertical alignment by adjusting for font size
  const textHeightOffset = invoiceFontSize * 0.55;
  const invoiceTextY = headerY + textHeightOffset;
  const invoiceX = pageWidth - padding;
  const invoiceText = documentConfig?.title && documentConfig?.title;
  const invoiceTextWidth = doc.getTextWidth(invoiceText);
  doc.text(invoiceText, invoiceX, invoiceTextY, { align: "right" });
  // If cancelled, show "CANCELLED" immediately to the right of "INVOICE"
  if (documentConfig?.status === "CANCELLED") {
    const cancelledFontSize = invoiceFontSize * 0.6;
    doc.setFontSize(cancelledFontSize);
    doc.setTextColor(220, 53, 69); // Bootstrap danger red
    doc.setFont("Inter-Bold", "bold");
    // Position CANCELLED right after INVOICE
    const cancelledX = invoiceX - invoiceTextWidth - 8; // 8 is spacing between words
    doc.text("CANCELLED", cancelledX, invoiceTextY, { align: "right" });
    // Reset styles
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(invoiceFontSize);
  }
  // Prepare font size for labels and values
  doc.setFontSize(10);
  const invoiceNoLabel = documentConfig?.numberLabel;
  const invoiceNoValue = documentConfig?.number;
  const invoiceDateLabel = "Date:";
  const invoiceDateValue = documentConfig?.Date.date;
  // Measure total widths
  const invoiceNoLabelWidth = doc.getTextWidth(invoiceNoLabel);
  const invoiceNoValueWidth = doc.getTextWidth(invoiceNoValue);
  const invoiceDateLabelWidth = doc.getTextWidth(invoiceDateLabel);
  const invoiceDateValueWidth = doc.getTextWidth(invoiceDateValue);
  const gap = 10;
  const innerGap = 2;
  const invoiceRowY = 25;
  const totalTextWidth =
    invoiceNoLabelWidth +
    innerGap +
    invoiceNoValueWidth +
    gap +
    invoiceDateLabelWidth +
    innerGap +
    invoiceDateValueWidth;
  const invoiceRowX = pageWidth - padding - totalTextWidth;
  const lineStartX = padding;
  const lineEndX = invoiceRowX - 5;
  const lineY = invoiceRowY;
  // Middle line
  doc.setDrawColor(selectedTheme?.templateTheme?.primaryColor);
  doc.setLineWidth(2);
  doc.setLineCap("round");
  doc.line(lineStartX, lineY, lineEndX, lineY);

  doc.setFont("Inter-Bold", "bold");
  doc.text(invoiceNoLabel, invoiceRowX, invoiceRowY);
  doc.setFont("Inter-Regular", "normal");
  doc.text(
    invoiceNoValue,
    invoiceRowX + invoiceNoLabelWidth + innerGap,
    invoiceRowY,
  );
  const dateX =
    invoiceRowX + invoiceNoLabelWidth + innerGap + invoiceNoValueWidth + gap;

  doc.setFont("Inter-Bold", "bold");
  doc.text(invoiceDateLabel, dateX, invoiceRowY);

  doc.setFont("Inter-Regular", "normal");
  doc.text(
    invoiceDateValue,
    dateX + invoiceDateLabelWidth + innerGap,
    invoiceRowY,
  );
  const sectionTop = 30;
  const col2X = padding;
  doc.setFontSize(8);
  const hasSaleToData =
    documentConfig?.toData &&
    (documentConfig?.toData?.name ||
      documentConfig?.toData?.addressline1 ||
      documentConfig?.toData?.addressline2 ||
      documentConfig?.toData?.district ||
      documentConfig?.toData?.country ||
      documentConfig?.toData?.pincode ||
      documentConfig?.toData?.phone ||
      documentConfig?.toData?.email ||
      documentConfig?.toData?.gstnumber ||
      documentConfig?.toData?.state ||
      documentConfig?.toData?.statecode ||
      documentConfig?.toData?.referenceid);

  const summaryWidth = 70;
  const summaryX = pageWidth - padding - summaryWidth;
  let summaryY = sectionTop;

  // FIX 1: Dynamic calculation to give customer details max available width before hitting the box
  const sectionWidth = summaryX - col2X - 8;

  // FIX 2: Adjusted Customer title to align perfectly with the "Summary" internal text row
  if (hasSaleToData) {
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.text(documentConfig?.toLabel, col2X, sectionTop + 4);
  }

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(8);

  // FIX 3: Set initial starting coordinates for details to match box layout flow
  let fromY = sectionTop + 10;
  let toY = sectionTop + 10;

  const sectionGap = 12;

  let toDetails = [];
  if (hasSaleToData) {
   if (hasSaleToData) {
  toDetails = [
    {
      bold: true,
      text: documentConfig?.toData?.name || "-",
    },

    {
      bold: false,
      text: [
        documentConfig?.toData?.addressline1,
        documentConfig?.toData?.addressline2,
        documentConfig?.toData?.district,
        documentConfig?.toData?.country,
        documentConfig?.toData?.pincode,
      ]
        .filter(Boolean)
        .join(", "),
    },

    ...(documentConfig?.toData?.phone
      ? [
          {
            bold: false,
            text: `Phone: ${documentConfig?.toData?.phone}`,
          },
        ]
      : []),

    ...(documentConfig?.toData?.email
      ? [
          {
            bold: false,
            text: `Email: ${documentConfig?.toData?.email}`,
          },
        ]
      : []),

    ...(documentConfig?.toData?.referenceid
      ? [
          {
            bold: false,
            text: "Reference No:",
            suffix: ` ${documentConfig?.toData?.referenceid}`,
          },
        ]
      : []),

    ...(documentConfig?.toData?.gstnumber
      ? [
          {
            bold: true,
            text: "GST:",
            suffix: ` ${documentConfig?.toData?.gstnumber}`,
          },
        ]
      : []),

    ...(documentConfig?.toData?.fssai
      ? [
          {
            bold: true,
            text: "FSSAI:",
            suffix: ` ${documentConfig?.toData?.fssai}`,
          },
        ]
      : []),

    ...(documentConfig?.toData?.cinnumber
      ? [
          {
            bold: true,
            text: "CIN:",
            suffix: ` ${documentConfig?.toData?.cinnumber}`,
          },
        ]
      : []),

    ...(documentConfig?.toData?.pannumber
      ? [
          {
            bold: true,
            text: "PAN:",
            suffix: ` ${documentConfig?.toData?.pannumber}`,
          },
        ]
      : []),

    ...(documentConfig?.toData?.state
      ? [
          {
            bold: true,
            text: "State:",
            suffix: ` ${documentConfig?.toData?.state} [ State Code: ${
              documentConfig?.toData?.statecode || ""
            } ]`,
          },
        ]
      : []),
  ];
}
  }
  const wrapText = (text, maxWidth) => doc.splitTextToSize(text, maxWidth);
  const lineHeight = 4.5; // Slightly increased for matching summary grid line heights

  const renderDetails = (details, startX, startY) => {
    let y = startY;
    details.forEach(({ bold, text, suffix = "" }) => {
      const fullText = text + suffix;
      const lines = wrapText(fullText, sectionWidth);
      lines.forEach((line, index) => {
        if (index === 0 && suffix && bold) {
          const labelWidth = doc.getTextWidth(text);
          doc.setFont("Inter-Bold", "bold");
          doc.text(text, startX, y);
          doc.setFont("Inter-Regular", "normal");
          doc.text(suffix, startX + labelWidth, y);
        } else {
          doc.setFont(
            bold ? "Inter-Bold" : "Inter-Regular",
            bold ? "bold" : "normal",
          );
          doc.text(line, startX, y);
        }
        y += lineHeight;
      });
    });

    return y;
  };

  let shippingY = sectionTop + 5;
  if (hasSaleToData) {
    toY = renderDetails(toDetails, col2X, toY);
  }

  const summaryHeight = 25;
  doc.setLineWidth(0.1);
  doc.setDrawColor(selectedTheme?.templateTheme?.primaryColor);
  const themeColor = selectedTheme?.templateTheme?.primaryColor || "#F0F5FF";
  doc.setFillColor(themeColor);
  doc.roundedRect(summaryX, summaryY, summaryWidth, summaryHeight, 2, 2, "FD");

  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(9);
  doc.setTextColor(50, 50, 50);
  doc.text(" Original Invoice Summary", summaryX + 4, summaryY + 5);

  doc.setLineWidth(0.15);
  doc.line(
    summaryX + 4,
    summaryY + 7.5,
    summaryX + summaryWidth - 4,
    summaryY + 7.5,
  );

  const summaryRows = [
    { label: "Sale Mode", value: documentConfig?.saleMode || "INVOICE" },
    { label: "Sale No", value: documentConfig?.saleNo || "-" },
    {
      label: "Sale Date",
      value:
        DateConverter.ISOToDisplayFormat(
          DateConverter.convertToTimeZone(
            documentConfig?.originalSaleDate,
            timezone,
          ),
        ).date || "-",
    },
  ];

  let rowY = summaryY + 12.5;
  const rowLineHeight = 4.5;

  summaryRows.forEach((row) => {
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(row.label, summaryX + 4, rowY);

    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0, 0, 0);

    const valWidth = doc.getTextWidth(row.value);
    doc.text(row.value, summaryX + summaryWidth - 4 - valWidth, rowY);

    rowY += rowLineHeight;
  });

  shippingY = summaryY + summaryHeight + 6;
  const maxSectionY = Math.max(fromY, toY, shippingY);

  // 3. Product Table
  const productBody = documentConfig?.returnItems?.map((item, i) => {
    const qty = item?.quantity || 1;
    const landingCost = item?.lineTotalLandingCost;
    const rate = item?.returnPrice;
    // const gross = item?.lineTotalSoldPrice;
    const taxable = item?.lineTotalBasePrice;
    const gstPercentage = item?.taxPercentage;
    const gstAmt = item?.lineTotalTaxAmount;
    const total = item?.lineGrandTotal;
    const desc = [
      `${item.title}`,
      ...(item?.itemRemarks ? [`"Remark" : ${item.itemRemarks}`] : []),
      ...(item?.itemCondition ? [`"Condition" : ${item.itemCondition}`] : []),
      ...(item?.imode === "PRODUCT" && item?.note ? [item?.note] : []),
      ...(item?.imode === "SERVICE" && item?.description
        ? [item?.description]
        : []),
      ...(item?.hsnsac
        ? [`${item?.hsnsac === "SERVICE" ? "SAC" : "HSN"} : ${item.hsnsac}`]
        : []),
    ].join("\n");
    return [
      i + 1,
      desc,
      `₹9,99,99,999.99`,
      `${qty} ${(() => {
        const unitCode = item?.isSecondaryUnit
          ? item?.unitConfig?.secondaryUnitCode
          : item?.unitConfig?.primaryUnitCode;

        // return unitCode !== "unit" ? unitCode : "";
        return unitCode || "";
      })()}`,
      // `${gross}`,
      `${taxable}`,
      `${gstPercentage}%`,
      `${gstAmt}`,
      `${total}`,
    ];
  });

  const soldTotal = documentConfig?.totalSoldPrice;

  const baseTotal = documentConfig?.totalBasePrice;

  const taxTotal = documentConfig?.totalTaxAmount;

  const grandTotal = documentConfig?.returnGrandTotalRounded;

  // // Add total row INSIDE the same table
  productBody.push([
    {
      content: "",
      colSpan: 2,
      styles: { fontStyle: "bold", font: "Inter-Bold" },
    },
    {
      content: "Total",
      styles: { halign: "right", fontStyle: "bold", font: "Inter-Bold" ,fontSize:7.5},
    },
    {
      content: "",
      styles: { fontStyle: "bold", font: "Inter-Bold" },
    },
    {
      content: `${baseTotal}`,
      styles: { fontStyle: "bold", font: "Inter-Bold" ,fontSize:7.5},
    },
    {
      content: ``,
      styles: { fontStyle: "bold", font: "Inter-Bold", },
    },
    {
      content: `${taxTotal}`,
      styles: { fontStyle: "bold", font: "Inter-Bold",fontSize:7.5 },
    },
    {
      content: `${grandTotal}`,
      styles: { fontStyle: "bold", font: "Inter-Bold",fontSize:7.5 },
    },
  ]);
  autoTable(doc, {
    startY: maxSectionY,
    head: [
      [
        "S.No",
        "Description",
        " Rate",
        "Qty",
        // "Gross Amount",
        "Tax Val",
        "GST(%)",
        "GST Amount",
        "Total Return",
      ],
    ],
    body: productBody,
    margin: {
      top: padding,
      left: padding,
      right: padding,
      bottom: pageH - HALF_PAGE_HEIGHT + padding,
    }, // add right margin if needed
    tableWidth: doc.internal.pageSize.getWidth() - 10,
    styles: {
      font: "Inter-Regular",
      fontStyle: "normal",
      fontSize: 8,
      halign: "middle",
      cellPadding: 2,
      lineColor: selectedTheme?.templateTheme?.borderColor,
      lineWidth: 0.2,
      textColor: "#041121",
    },
    headStyles: {
      fillColor: selectedTheme?.templateTheme?.primaryColor,
      textColor: selectedTheme.templateTheme?.foregroundColor,
      fontStyle: "bold",
      font: "Inter-Bold",
      halign: "center",
      valign: "middle",
    },
    columnStyles: {
      0: { cellWidth: 13, halign: "center", valign: "middle" },
      1: {
        cellWidth: "auto",
        halign: "left",
        styles: {
          overflow: "linebreak",
        },
      },
      2: { cellWidth: 25, halign: "right" },
      3: { cellWidth: 13, halign: "center" },
      4: { cellWidth: 25, halign: "right" },
      5: { cellWidth: 18, halign: "right" },
      6: { cellWidth: 19, halign: "right" },
      7: { cellWidth: 25, halign: "right" },
    },

    theme: "grid",
  });
  const body = [
    [
      {
        content: `Total Amount In Words : ${documentConfig?.amountInWords}`,
        styles: {
          fontStyle: "bold",
          font: "Inter-Bold",
          fontSize: 9,
          valign: "top",
        },
      },
      {
        content: "Grand Total (Rounded)",
        styles: { halign: "right", fontSize: 9 },
      },
      {
        content: `${grandTotal}`,
        styles: { halign: "right", valign: "middle", fontSize: 7.5 },
      },
    ],
  ];

  const afterProductsY = doc.lastAutoTable?.finalY + 0.3;
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginBottom = 20;
  const estimatedSummaryHeight = 50;
  let summaryStartY;
  let isSamePage = true;
  if (afterProductsY + estimatedSummaryHeight + marginBottom <= pageHeight) {
    summaryStartY = afterProductsY;
  } else {
    doc.addPage();
    summaryStartY = 20;
    isSamePage = false;
  }
  autoTable(doc, {
    startY: summaryStartY,
    body: body,
    pageBreak: "avoid",
    margin: {
      top: padding,
      left: padding,
      right: padding,
      bottom: pageH - HALF_PAGE_HEIGHT + padding,
    },
    tableWidth: doc.internal.pageSize.getWidth() - 10,
    styles: {
      font: "Inter-Regular",
      fontStyle: "normal",
      halign: "middle",
      cellPadding: 2,
      fontSize: 9,
      lineColor: selectedTheme?.templateTheme?.borderColor,
      lineWidth: 0.2,
      fillColor: selectedTheme?.templateTheme?.primaryColor,
      textColor: selectedTheme.templateTheme?.foregroundColor,
    },
    columnStyles: {
      0: { cellWidth: 113 },
      1: { cellWidth: 62, halign: "right" },
      2: { cellWidth: 25, halign: "center", valign: "middle" },
    },
    didParseCell: function (data) {
      if (data.row.index === 0 && isSamePage) {
        data.cell.styles.lineWidth = {
          top: 0,
          right: 0.2,
          bottom: 0.2,
          left: 0.2,
        };
      }
      const lastRowIndex = body.length - 1;
      if (data.section === "body") {
        if (
          data.row.index === lastRowIndex &&
          (data.column.index === 1 || data.column.index === 2)
        ) {
          // data.cell.styles.fillColor = "#f4f4f6";
          // data.cell.styles.textColor = [0, 0, 0];
          data.cell.styles.font = "Inter-Bold";
          data.cell.styles.fontStyle = "bold";
        } else {
          data.cell.styles.textColor = [0, 0, 0];
          data.cell.styles.fillColor = "#ffffff";
          data.cell.styles.font = "Inter-Regular";
          data.cell.styles.fontStyle = "normal";
        }
      }
    },
    theme: "grid",
  });
  const afterSummaryY = doc.lastAutoTable.finalY;
  const pageHeight2 = doc.internal.pageSize.getHeight();
  const marginBottom2 = 20;
  const declarationHeight = 45;
  let finalY;
  if (afterSummaryY + declarationHeight + marginBottom2 > pageHeight2) {
    doc.addPage();
    finalY = 10;
  } else {
    finalY = afterSummaryY;
  }
  let currentY = finalY + 2;
  // ===================== Remark =====================
  if (documentConfig?.remarks) {
    // Page break check
    if (currentY > pageHeight - 35) {
      doc.addPage();
      currentY = padding + 5;
    }

    const remarkX = padding;
    const innerPaddingX = 2;
    const remarkWidth = pageWidth - padding * 2 - innerPaddingX * 2 + 5;

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.setTextColor(72, 72, 72);
    doc.text("Remark", remarkX, currentY);

    const remarkLines = doc.splitTextToSize(
      capitalizeFirstLetter(documentConfig.remarks),
      remarkWidth + 30,
    );

    const lineHeight = 4;
    const paddingTop = 2.5;
    const paddingBottom = 2.5;

    const remarkHeight =
      paddingTop + remarkLines.length * lineHeight + paddingBottom;

    const boxY = currentY + 4;

    // Border
    doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
    doc.setLineWidth(0.2);
    // Remark Text
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);
    doc.setTextColor(0);

    let textY = boxY + paddingTop - 1;

    remarkLines.forEach((line) => {
      doc.text(line, remarkX, textY);
      textY += lineHeight;
    });

    // Move below remark
    currentY = boxY + remarkHeight + 4;
  }

  const rightX = pageWidth - padding;

  const boxTopY = currentY + 3;

  const SIGN_Y = boxTopY; // Signature image
  let LABEL_Y = SIGN_Y + 8; // "Issued By, Signature"

  // // Fonts
  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(8);

  // // -------- 1️⃣ Signature Image FIRST ----------
  const signimg = uRLs.IMAGE_URL + storeSettings?.invoiceSettings?.signature;

  if (storeSettings?.invoiceSettings?.signature) {
    try {
      const response = await fetch(signimg);
      const blob = await response.blob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const { width, height } = await loadImageDimensions(base64);

      const maxWidth = 50;
      const maxHeight = 10;

      const ratio = Math.min(maxWidth / width, maxHeight / height);
      const imgWidth = width * ratio;
      const imgHeight = height * ratio;

      const format = signimg.endsWith(".png") ? "PNG" : "JPEG";
      const imgX = rightX - imgWidth;

      doc.addImage(base64, format, imgX, SIGN_Y, imgWidth, imgHeight);
    } catch (err) {
      console.warn("Image failed to load:", err);
    }
  }
  LABEL_Y += 4;
  doc.text("Issued By, Signature", rightX, LABEL_Y, { align: "right" });
  currentY += 2;
  if (storeSettings?.negobillBranding) {
    currentY = LABEL_Y + 5;

    const normalText = "Powered by ";
    const boldText = "NegoBill.com";

    doc.setFont("Inter-Regular", "normal");
    const normalW = doc.getTextWidth(normalText);

    doc.setFont("Inter-Bold", "bold");
    const boldW = doc.getTextWidth(boldText);

    const totalWidth = normalW + boldW;

    // Center horizontally
    const startX = (pageWidth - totalWidth) / 2;

    doc.setFont("Inter-Regular", "normal");
    doc.text(normalText, startX, currentY);

    doc.setFont("Inter-Bold", "bold");
    doc.text(boldText, startX + normalW, currentY);
  }
  doc.setFontSize(7);
  // setDownload(false);
  // setPrint(false);
  return doc;
};
const ReturnSaleInvoice = ({
  globalData,
  terms,
  qrCodeDataUrl,  
  selectedTheme,
  invoiceConfig,
}) => {  
  const { documentConfig, selectedSize, timezone } = invoiceConfig;
  const { storeSettings } = usePermission();  
  return (
    <>
      {selectedSize === "HALFA4" ? (
        <>
          <>
            <div className="tm_container">
              <div className="tm_invoice_wrap">
                <div className="tm_invoice tm_style1 tm_type1">
                  <div className="tm_invoice_in p-2">
                    <table width="100%" className="pdfcontent">
                      <tbody>
                        <tr>
                          <td width="50%" style={{ textAlign: "left" }}>
                            <div className="tm_logo">
                              {documentConfig?.image && (
                                <img
                                  src={                                    
                                    documentConfig?.image
                                  }
                                  className="img-rounded logo mb-0"
                                  height="30"
                                />
                              )}
                            </div>
                          </td>
                          <td width="50%" style={{ textAlign: "right" }}>
                            <div className="tm_primary_color font-20 fw-bold tm_text_uppercase">
                              {documentConfig?.title}
                            </div>
                            {globalData?.status === "CANCELLED" && (
                              <span className="badge rounded-pill bg-label-danger mt-3 fs-15 tm_text_uppercase">
                                {globalData?.status}
                              </span>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="tm_invoice_info tm_mb20">
                      <div
                        className="tm_invoice_seperator tm_gray_bg"
                        style={{
                          backgroundColor:
                            selectedTheme?.templateTheme?.primaryColor,
                        }}
                      ></div>
                      <div className="tm_invoice_info_list">
                        <p className="tm_invoice_number tm_m0 mb-0 mt-3">
                          <b className="tm_primary_color">
                            {documentConfig?.numberLabel}{" "}
                          </b>{" "}
                          {documentConfig.number}
                        </p>
                        <p className="tm_invoice_date tm_m0 mt-3">
                          <b className="tm_primary_color"> Date: </b>{" "}
                          {documentConfig?.Date.date}
                        </p>
                      </div>
                    </div>
                    <table width="100%" className="pdfcontent">
                      <tbody>
                        <tr>
                          <div className="row">
                            {/* <td width="5%"></td> */}
                            {documentConfig?.toData && (
                              <div
                                className="col-md-7 col-12"
                                style={{ verticalAlign: "top" }}
                              >
                                {documentConfig?.toData &&
                                  (documentConfig?.toData?.name ||
                                    documentConfig?.toData?.addressline1 ||
                                    documentConfig?.toData?.addressline2 ||
                                    documentConfig?.toData?.state ||
                                    documentConfig?.toData?.district ||
                                    documentConfig?.toData?.country ||
                                    documentConfig?.toData?.pincode ||
                                    documentConfig?.toData?.phone ||
                                    documentConfig?.toData?.email ||
                                    documentConfig?.toData?.gstnumber ||
                                    documentConfig?.toData?.statecode ||
                                    documentConfig?.toData?.referenceid) && (
                                    <div>
                                      <div
                                        style={{
                                          fontSize: "14px",
                                          lineHeight: "22px",
                                          fontWeight: "600",
                                          marginBottom: "0px",
                                        }}
                                      >
                                        {documentConfig?.toLabel}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          marginBottom: "0px",
                                        }}
                                      >
                                        {documentConfig?.toData?.name || ""}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          lineHeight: "18px",
                                          marginBottom: "0px",
                                        }}
                                      >
                                        <div>
                                          {documentConfig?.toData
                                            ?.addressline1 || ""}{" "}
                                          {documentConfig?.toData
                                            ?.addressline2 || ""}{" "}
                                          {documentConfig?.toData?.state || ""}{" "}
                                          {documentConfig?.toData?.district ||
                                            ""}{" "}
                                          {documentConfig?.toData?.country ||
                                            ""}{" "}
                                          {documentConfig?.toData?.country
                                            ? " - "
                                            : ""}{" "}
                                          {documentConfig?.toData?.pincode ||
                                            ""}
                                        </div>
                                        {documentConfig?.toData?.phone && (
                                          <div>
                                            Phone: +91{" "}
                                            {documentConfig?.toData?.phone}
                                          </div>
                                        )}
                                        {documentConfig?.toData?.email && (
                                          <div>
                                            Email:{" "}
                                            {documentConfig?.toData?.email}
                                          </div>
                                        )}
                                        {documentConfig?.toData
                                          ?.referenceid && (
                                          <div>
                                            Reference No:{" "}
                                            {
                                              documentConfig?.toData
                                                ?.referenceid
                                            }
                                          </div>
                                        )}
                                        {documentConfig?.toData?.gstnumber && (
                                          <div>
                                            <strong>GST:</strong>{" "}
                                            {documentConfig?.toData?.gstnumber}
                                          </div>
                                        )}
                                        {documentConfig?.toData?.fssai && (
                                          <div>
                                            <strong>FASSI:</strong>{" "}
                                            {documentConfig?.toData?.fssai}
                                          </div>
                                        )}
                                        {documentConfig?.toData?.cinnumber && (
                                          <div>
                                            <strong>CIN:</strong>{" "}
                                            {documentConfig?.toData?.cinnumber}
                                          </div>
                                        )}
                                        {documentConfig?.toData?.pannumber && (
                                          <div>
                                            <strong>PAN:</strong>{" "}
                                            {documentConfig?.toData?.pannumber}
                                          </div>
                                        )}
                                        {documentConfig?.toData?.state && (
                                          <div>
                                            <strong>State:</strong>{" "}
                                            {documentConfig?.toData?.state} [
                                            State Code :
                                            {documentConfig?.toData
                                              ?.statecode || ""}{" "}
                                            ]
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            )}
                            <div className="col-md-4 col-12 ms-auto">
                              <div
                                style={{
                                  border: "1px solid #d6d6d6",
                                  borderRadius: "6px",
                                  background:
                                    selectedTheme?.templateTheme?.primaryColor,
                                  padding: "6px 12px",
                                  fontSize: "11px",
                                }}
                              >
                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: "12px",
                                    marginBottom: "4px",
                                    borderBottom: "1px solid #ddd",
                                    paddingBottom: "3px",
                                    color: "#b14444",
                                  }}
                                >
                                  Original Invoice Summary
                                </div>

                                <table
                                  style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                  }}
                                >
                                  <tbody>
                                    <tr>
                                      <td
                                        style={{
                                          textAlign: "left",
                                          padding: "2px 0",
                                          color: "#646464",
                                        }}
                                      >
                                        {" "}
                                        {/* Reduced padding from 4px to 2px */}
                                        Sale Mode
                                      </td>
                                      <td
                                        style={{
                                          textAlign: "right",
                                          padding: "2px 0",
                                          color: "#000",
                                        }}
                                      >
                                        <strong>
                                          {documentConfig?.saleMode || "-"}
                                        </strong>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td
                                        style={{
                                          textAlign: "left",
                                          padding: "2px 0",
                                          color: "#646464",
                                        }}
                                      >
                                        Sale No
                                      </td>
                                      <td
                                        style={{
                                          textAlign: "right",
                                          padding: "2px 0",
                                          color: "#000",
                                        }}
                                      >
                                        <strong>
                                          {documentConfig?.saleNo || "-"}
                                        </strong>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td
                                        style={{
                                          textAlign: "left",
                                          padding: "2px 0",
                                          color: "#646464",
                                        }}
                                      >
                                        Sale Date
                                      </td>
                                      <td
                                        style={{
                                          textAlign: "right",
                                          padding: "2px 0",
                                          color: "#000",
                                        }}
                                      >
                                        {documentConfig?.originalSaleDate &&(
                                        <strong>
                                          {DateConverter.ISOToDisplayFormat(
                                            DateConverter.convertToTimeZone(
                                              documentConfig?.originalSaleDate,
                                              timezone,
                                            ),
                                          ).date || "-"}
                                        </strong>
                                        )}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </tr>
                      </tbody>
                    </table>
                    <table
                      // className="items"
                      width="100%"
                      style={{
                        marginTop: "6px",
                        borderCollapse: "collapse",
                      }}
                      cellPadding="4"
                    >
                      <thead>
                        <tr>
                          <th
                            className="force-style"
                            style={{
                              width: "20px",
                              textAlign: "center",
                              "--th-color":
                                selectedTheme?.templateTheme?.foregroundColor,
                              "--th-bg":
                                selectedTheme?.templateTheme?.primaryColor,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            S.No
                          </th>
                          <th
                            className="force-style"
                            style={{
                              width: "240px",
                              textAlign: "center",
                              "--th-color":
                                selectedTheme?.templateTheme?.foregroundColor,
                              "--th-bg":
                                selectedTheme?.templateTheme?.primaryColor,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            Description
                          </th>
                          <th
                            className="force-style"
                            style={{
                              width: "120px",
                              textAlign: "center",
                              "--th-color":
                                selectedTheme?.templateTheme?.foregroundColor,
                              "--th-bg":
                                selectedTheme?.templateTheme?.primaryColor,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            Rate
                          </th>
                          <th
                            className="force-style"
                            style={{
                              width: "50px",
                              textAlign: "center",
                              "--th-color":
                                selectedTheme?.templateTheme?.foregroundColor,
                              "--th-bg":
                                selectedTheme?.templateTheme?.primaryColor,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            Qty
                          </th>
                          {/* <th
                                            className="force-style"
                                            style={{
                                              width: "100px",
                                              textAlign: "center",
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                            }}
                                          >
                                            Gross Amount
                                          </th> */}
                          <th
                            className="force-style"
                            style={{
                              width: "100px",
                              textAlign: "center",
                              "--th-color":
                                selectedTheme?.templateTheme?.foregroundColor,
                              "--th-bg":
                                selectedTheme?.templateTheme?.primaryColor,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            Tax Val
                          </th>
                          <th
                            className="force-style"
                            style={{
                              width: "30px",
                              textAlign: "center",
                              "--th-color":
                                selectedTheme?.templateTheme?.foregroundColor,
                              "--th-bg":
                                selectedTheme?.templateTheme?.primaryColor,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            GST(%)
                          </th>
                          <th
                            className="force-style"
                            style={{
                              width: "100px",
                              textAlign: "center",
                              "--th-color":
                                selectedTheme?.templateTheme?.foregroundColor,
                              "--th-bg":
                                selectedTheme?.templateTheme?.primaryColor,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            GST Amount
                          </th>
                          <th
                            className="force-style"
                            style={{
                              width: "100px",
                              textAlign: "center",
                              "--th-color":
                                selectedTheme?.templateTheme?.foregroundColor,
                              "--th-bg":
                                selectedTheme?.templateTheme?.primaryColor,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            Total Return
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {documentConfig?.returnItems?.map((product, index) => (
                          <tr key={index}>
                            <td
                              className="force-style"
                              style={{
                                textAlign: "center",
                                fontSize: "12px",
                                "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                            >
                              {index + 1}
                            </td>
                            <td
                              className="force-style"
                              style={{
                                textAlign: "left",
                                fontSize: "12px",
                                width: "30%",
                                "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                            >
                              <div>{product.title}</div>
                              {product?.itemRemarks && (
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#555",
                                    marginTop: "2px",
                                  }}
                                >
                                  Remark: {product.itemRemarks}
                                </div>
                              )}

                              {product?.itemCondition && (
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#555",
                                  }}
                                >
                                  Condition: {product.itemCondition}
                                </div>
                              )}
                              {product.imode === "SERVICE" && (
                                <div className="ms-0 fsz-10 lh-13 mb-1">
                                  {product.description}
                                </div>
                              )}
                              {product.imode === "PRODUCT" && product.note && (
                                <div className="ms-0 fsz-10 mb-1">
                                  {product?.note}
                                </div>
                              )}
                              {product?.hsnsac && (
                                <div>
                                  <span className="fw-medium">
                                    {product?.imode === "SERVICE"
                                      ? "SAC:"
                                      : "HSN:"}
                                  </span>{" "}
                                  {product?.hsnsac}
                                </div>
                              )}
                            </td>
                            <td
                              className="force-style"
                              style={{
                                textAlign: "right",
                                fontSize: "12px",
                                "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                            >
                              {product?.returnPrice}
                            </td>
                            <td
                              className="force-style"
                              style={{
                                textAlign: "center",
                                fontSize: "12px",
                                "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                            >
                              {product?.quantity}
                              <span className="font-11 ms-1">
                                {(() => {
                                  const unitCode = product?.isSecondaryUnit
                                    ? product?.unitConfig?.secondaryUnitCode
                                    : product?.unitConfig?.primaryUnitCode;

                                  // return unitCode !== "unit" ? unitCode : "";
                                  return unitCode || "";
                                })()}
                              </span>
                            </td>
                            {/* <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                                                }}
                                              >
                                                {product?.lineTotalSoldPrice}
                                              </td> */}
                            <td
                              className="force-style"
                              style={{
                                textAlign: "right",
                                fontSize: "12px",
                                "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                            >
                              {product?.lineTotalBasePrice}
                            </td>
                            <td
                              className="force-style"
                              style={{
                                textAlign: "center",
                                fontSize: "12px",
                                "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                            >
                              {product.taxPercentage}%
                            </td>
                            <td
                              className="force-style"
                              style={{
                                textAlign: "right",
                                fontSize: "12px",
                                "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                            >
                              {product?.lineTotalTaxAmount}
                            </td>
                            <td
                              className="force-style"
                              style={{
                                textAlign: "right",
                                fontSize: "12px",
                                "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                            >
                              {product?.lineGrandTotal}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td
                            colSpan={2}
                            className="force-style"
                            style={{
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          ></td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              fontWeight: 600,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            Total
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              fontWeight: 600,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            {/* {globalData?.totalqty} */}
                          </td>
                          {/* 
                                          <td
                                            className="force-style"
                                            style={{
                                              textAlign: "center",
                                              fontSize: "12px",
                                              fontWeight: 600,
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                                            }}
                                          >
                                            {globalData?.totalsoldprice}
                                          </td> */}
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              fontWeight: 600,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            {documentConfig?.totalBasePrice}
                          </td>
                          <td></td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              fontWeight: 600,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            {documentConfig?.totalTaxAmount}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              fontWeight: 600,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            {documentConfig?.grandTotal}
                          </td>
                        </tr>
                        <tr>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "left",
                              fontSize: "12px",
                              fontWeight: 600,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                            colSpan="4"
                          >
                            Total Amount In Words :{" "}
                            {documentConfig?.amountInWords}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "14px",
                              fontWeight: 600,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                              "--th-color":
                                selectedTheme?.templateTheme?.foregroundColor,
                              "--th-bg":
                                selectedTheme?.templateTheme?.primaryColor,
                            }}
                            colSpan="3"
                          >
                            Grand Total (Rounded)
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "14px",
                              fontWeight: 600,
                              "--th-color":
                                selectedTheme?.templateTheme?.foregroundColor,
                              "--th-bg":
                                selectedTheme?.templateTheme?.primaryColor,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            {documentConfig?.grandTotal}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    {parseAmount(documentConfig?.totalDiscount) > 0 && (
                      <table width="100%">
                        <tbody>
                          <tr>
                            <td
                              style={{
                                fontSize: "13px",
                                fontWeight: 600,
                                textAlign: "center",
                                padding: "8px",
                                color: "black",
                              }}
                            >
                              Your Total Discount : 
                              {documentConfig?.totalDiscount}
                              {/* <br />
                                    <div className="mt-1 fs-20">
                                      ***** Thank You! Visit Us Again *****
                                    </div> */}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                    {/* {(documentConfig?.payDetails?.CASH > 0 ||
                            payDetails?.CARD > 0 ||
                            payDetails?.ONLINE > 0 ||
                            payDetails?.UPI > 0 ||
                            payDetails?.PAYLATER > 0) && (
                            <table
                              className="items mt-2"
                              width="38%"
                              cellPadding="4"
                            >
                              <thead>
                                <tr>
                                  <th
                                    colSpan={2}
                                    className="force-style"
                                    style={{
                                      width: "100%",
                                      textAlign: "center",
                                      "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      "--th-bg":
                                        selectedTheme?.templateTheme
                                          ?.primaryColor,
                                      "--th-color":
                                        selectedTheme?.templateTheme
                                          ?.foregroundColor,
                                    }}
                                  >
                                    Refund Method
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {payDetails?.CASH > 0 && (
                                  <tr>
                                    <td
                                      className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      Cash
                                    </td>
                                    <td
                                      className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      
                                      {CurrencyConverter.toCurrency(
                                        payDetails?.CASH || 0,
                                        "INR",
                                      )}
                                    </td>
                                  </tr>
                                )}
                                {payDetails?.CARD > 0 && (
                                  <tr>
                                    <td
                                      className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      Card
                                    </td>
                                    <td
                                      className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      
                                      {CurrencyConverter.toCurrency(
                                        payDetails?.CARD || 0,
                                        "INR",
                                      )}
                                    </td>
                                  </tr>
                                )}
                                {payDetails?.ONLINE > 0 && (
                                  <tr>
                                    <td
                                      className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      OnlineAmount
                                    </td>
                                    <td
                                      className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        fontWeight: "600",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      
                                      {CurrencyConverter.toCurrency(
                                        payDetails?.ONLINE || 0,
                                        "INR",
                                      )}
                                    </td>
                                  </tr>
                                )}
                                {payDetails?.UPI > 0 && (
                                  <tr>
                                    <td
                                      className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      UPI
                                    </td>
                                    <td
                                      className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        fontWeight: "600",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      
                                      {CurrencyConverter.toCurrency(
                                        payDetails?.UPI || 0,
                                        "INR",
                                      )}
                                    </td>
                                  </tr>
                                )}
                                {payDetails?.PAYLATER > 0 && (
                                  <tr>
                                    <td
                                      className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      BalanceDue
                                    </td>
                                    <td
                                      className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        fontWeight: "600",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      }}
                                    >
                                      
                                      {CurrencyConverter.toCurrency(
                                        payDetails?.PAYLATER || 0,
                                        "INR",
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          )} */}
                    {documentConfig?.remarks && (
                      <table width="100%" className="mb-2 mt-2">
                        <tbody>
                          <tr>
                            <td width="100%">
                              <div className="font-12 fw-600 align-left text-black mb-1">
                                Remark
                              </div>

                              <div
                                className="font-12 p-0"
                                style={{
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  minHeight: "30px",
                                }}
                              >
                                {capitalizeFirstLetter(documentConfig?.remarks)}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}

                    <table width="100%">
                      <tbody>
                        <tr>
                          <td
                            style={{
                              padding: "8px",
                              marginTop: "15px",
                            }}
                            className="d-flex align-items-start justify-content-end"
                          >
                            <div>
                              {storeSettings?.invoiceSettings?.signature && (
                                <div className="text-end p-0 fw-bold ">
                                  <img
                                    src={
                                      uRLs.IMAGE_URL +
                                      storeSettings?.invoiceSettings?.signature
                                    }
                                    width={150}
                                  />
                                </div>
                              )}
                              <div className="text-end p-0">
                                Issued By,Signature
                              </div>
                              <div className="text-end p-0 fw-bold ">
                                {documentConfig?.fromData?.title}
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    {storeSettings?.negobillBranding && (
                      <>
                        <div className=" fs-20 d-flex justify-content-center mt-1 ">
                          <span className="me-1">Powered By</span>{" "}
                          <strong>www.NegoBill.com</strong>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        </>
      ) : (
        <>
          <div className="tm_container">
            <div className="tm_invoice_wrap">
              <div className="tm_invoice tm_style1 tm_type1">
                <div className="tm_invoice_in p-2">
                  <table width="100%" className="pdfcontent">
                    <tbody>
                      <tr>
                        <td width="50%" style={{ textAlign: "left" }}>
                          <div className="tm_logo">
                            {documentConfig?.image && (
                              <img
                                src={
                                  documentConfig?.image
                                }
                                className="img-rounded logo mb-0"
                                height="30"
                              />
                            )}
                          </div>
                        </td>
                        <td width="50%" style={{ textAlign: "right" }}>
                          <div className="tm_primary_color font-20 fw-bold tm_text_uppercase">
                            <>{documentConfig?.title}</>
                          </div>
                          {globalData?.status === "CANCELLED" && (
                            <span className="badge rounded-pill bg-label-danger mt-3 fs-15 tm_text_uppercase">
                              {globalData?.status}
                            </span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="tm_invoice_info tm_mb20">
                    <div
                      className="tm_invoice_seperator tm_gray_bg"
                      style={{
                        backgroundColor:
                          selectedTheme?.templateTheme?.primaryColor,
                      }}
                    ></div>
                    <div className="tm_invoice_info_list">
                      <p className="tm_invoice_number tm_m0 mb-0 mt-3">
                        <b className="tm_primary_color">
                          {documentConfig?.numberLabel}{" "}
                        </b>{" "}
                        {documentConfig?.number}
                      </p>
                      <p className="tm_invoice_date tm_m0 mt-3">
                        <b className="tm_primary_color"> Date: </b>{" "}
                        {documentConfig?.Date.date}
                      </p>
                    </div>
                  </div>
                  <table width="100%" className="pdfcontent">
                    <tbody>
                      <tr>
                        <div className="row">
                          {documentConfig?.toData && (
                            <div
                              className="col-md-7 col-12"
                              style={{ verticalAlign: "top" }}
                            >
                              {documentConfig?.toData &&
                                (documentConfig?.toData?.name ||
                                  documentConfig?.toData?.addressline1 ||
                                  documentConfig?.toData?.addressline2 ||
                                  documentConfig?.toData?.state ||
                                  documentConfig?.toData?.district ||
                                  documentConfig?.toData?.country ||
                                  documentConfig?.toData?.pincode ||
                                  documentConfig?.toData?.phone ||
                                  documentConfig?.toData?.email ||
                                  documentConfig?.toData?.gstnumber ||
                                  documentConfig?.toData?.statecode ||
                                  documentConfig?.toData?.referenceid) && (
                                  <div>
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        lineHeight: "22px",
                                        fontWeight: "600",
                                        marginBottom: "0px",
                                      }}
                                    >
                                      {documentConfig?.toLabel}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        marginBottom: "0px",
                                      }}
                                    >
                                      {documentConfig?.toData?.name || ""}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        lineHeight: "18px",
                                        marginBottom: "0px",
                                      }}
                                    >
                                      <div>
                                        {documentConfig?.toData?.addressline1 ||
                                          ""}{" "}
                                        {documentConfig?.toData?.addressline2 ||
                                          ""}{" "}
                                        {documentConfig?.toData?.state || ""}{" "}
                                        {documentConfig?.toData?.district || ""}{" "}
                                        {documentConfig?.toData?.country || ""}{" "}
                                        {documentConfig?.toData?.country
                                          ? " - "
                                          : ""}{" "}
                                        {documentConfig?.toData?.pincode || ""}
                                      </div>
                                      {documentConfig?.toData?.phone && (
                                        <div>
                                          Phone: +91{" "}
                                          {documentConfig?.toData?.phone}
                                        </div>
                                      )}
                                      {documentConfig?.toData?.email && (
                                        <div>
                                          Email: {documentConfig?.toData?.email}
                                        </div>
                                      )}
                                      {documentConfig?.toData?.referenceid && (
                                        <div>
                                          Reference No:{" "}
                                          {documentConfig?.toData?.referenceid}
                                        </div>
                                      )}
                                      {documentConfig?.toData?.gstnumber && (
                                        <div>
                                          <strong>GST:</strong>{" "}
                                          {documentConfig?.toData?.gstnumber}
                                        </div>
                                      )}
                                      {documentConfig?.toData?.fssai && (
                                        <div>
                                          <strong>FASSI:</strong>{" "}
                                          {documentConfig?.toData?.fssai}
                                        </div>
                                      )}
                                      {documentConfig?.toData?.cinnumber && (
                                        <div>
                                          <strong>CIN:</strong>{" "}
                                          {documentConfig?.toData?.cinnumber}
                                        </div>
                                      )}
                                      {documentConfig?.toData?.pannumber && (
                                        <div>
                                          <strong>PAN:</strong>{" "}
                                          {documentConfig?.toData?.pannumber}
                                        </div>
                                      )}
                                      {documentConfig?.toData?.state && (
                                        <div>
                                          <strong>State:</strong>{" "}
                                          {documentConfig?.toData?.state} [
                                          State Code :
                                          {documentConfig?.toData?.statecode ||
                                            ""}{" "}
                                          ]
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                            </div>
                          )}
                          <div className="col-md-4 col-12 ms-auto">
                            <div
                              style={{
                                border: "1px solid #d6d6d6",
                                borderRadius: "6px",
                                background:
                                  selectedTheme?.templateTheme?.primaryColor,
                                padding: "6px 12px",
                                fontSize: "11px",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: "12px",
                                  marginBottom: "4px",
                                  borderBottom: "1px solid #ddd",
                                  paddingBottom: "3px",
                                  color: "#b14444",
                                }}
                              >
                                Original Invoice Summary
                              </div>

                              <table
                                style={{
                                  width: "100%",
                                  borderCollapse: "collapse",
                                }}
                              >
                                <tbody>
                                  <tr>
                                    <td
                                      style={{
                                        textAlign: "left",
                                        padding: "2px 0",
                                        color: "#646464",
                                      }}
                                    >
                                      {" "}
                                      {/* Reduced padding from 4px to 2px */}
                                      Sale Mode
                                    </td>
                                    <td
                                      style={{
                                        textAlign: "right",
                                        padding: "2px 0",
                                        color: "#000",
                                      }}
                                    >
                                      <strong>
                                        {documentConfig?.saleMode || "-"}
                                      </strong>
                                    </td>
                                  </tr>
                                  <tr>
                                    <td
                                      style={{
                                        textAlign: "left",
                                        padding: "2px 0",
                                        color: "#646464",
                                      }}
                                    >
                                      Sale No
                                    </td>
                                    <td
                                      style={{
                                        textAlign: "right",
                                        padding: "2px 0",
                                        color: "#000",
                                      }}
                                    >
                                      <strong>
                                        {documentConfig?.saleNo || "-"}
                                      </strong>
                                    </td>
                                  </tr>
                                  <tr>
                                    <td
                                      style={{
                                        textAlign: "left",
                                        padding: "2px 0",
                                        color: "#646464",
                                      }}
                                    >
                                      Sale Date
                                    </td>
                                    <td
                                      style={{
                                        textAlign: "right",
                                        padding: "2px 0",
                                        color: "#000",
                                      }}
                                    >
                                      
                                         {documentConfig?.originalSaleDate &&(
                                        <strong>
                                          {DateConverter.ISOToDisplayFormat(
                                            DateConverter.convertToTimeZone(
                                              documentConfig?.originalSaleDate,
                                              timezone,
                                            ),
                                          ).date || "-"}
                                        </strong>
      )}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </tr>
                    </tbody>
                  </table>
                  <table
                    // className="items"
                    width="100%"
                    style={{
                      marginTop: "6px",
                      borderCollapse: "collapse",
                    }}
                    cellPadding="4"
                  >
                    <thead>
                      <tr>
                        <th
                          className="force-style"
                          style={{
                            width: "20px",
                            textAlign: "center",
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          S.No
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "240px",
                            textAlign: "center",
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Description
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "120px",
                            textAlign: "center",
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Rate
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "50px",
                            textAlign: "center",
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Qty
                        </th>
                        {/* <th
                                            className="force-style"
                                            style={{
                                              width: "100px",
                                              textAlign: "center",
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                            }}
                                          >
                                            Gross Amount
                                          </th> */}
                        <th
                          className="force-style"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Tax Val
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "30px",
                            textAlign: "center",
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          GST(%)
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          GST Amount
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Total Return
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentConfig?.returnItems?.map((product, index) => (
                        <tr key={index}>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            {index + 1}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "left",
                              fontSize: "12px",
                              width: "30%",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            <div>{product.title}</div>
                            {product?.itemRemarks && (
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#555",
                                  marginTop: "2px",
                                }}
                              >
                                Remark: {product.itemRemarks}
                              </div>
                            )}

                            {product?.itemCondition && (
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#555",
                                }}
                              >
                                Condition: {product.itemCondition}
                              </div>
                            )}
                            {product.imode === "SERVICE" && (
                              <div className="ms-0 fsz-10 lh-13 mb-1">
                                {product.description}
                              </div>
                            )}
                            {product.imode === "PRODUCT" && product.note && (
                              <div className="ms-0 fsz-10 mb-1">
                                {product?.note}
                              </div>
                            )}
                            {product?.hsnsac && (
                              <div>
                                <span className="fw-medium">
                                  {product?.imode === "SERVICE"
                                    ? "SAC:"
                                    : "HSN:"}
                                </span>{" "}
                                {product?.hsnsac}
                              </div>
                            )}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            {product?.returnPrice}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            {product?.quantity}
                            <span className="font-11 ms-1">
                              {(() => {
                                const unitCode = product?.isSecondaryUnit
                                  ? product?.unitConfig?.secondaryUnitCode
                                  : product?.unitConfig?.primaryUnitCode;

                                // return unitCode !== "unit" ? unitCode : "";
                                return unitCode || "";
                              })()}
                            </span>
                          </td>
                          {/* <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                                                }}
                                              >
                                                {product?.lineTotalSoldPrice}
                                              </td> */}
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            {product?.lineTotalBasePrice}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            {product.taxPercentage}%
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            {product?.lineTotalTaxAmount}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            {product?.lineGrandTotal}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td
                          colSpan={2}
                          className="force-style"
                          style={{
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            fontWeight: 600,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        >
                          Total
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            fontWeight: 600,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        >
                          {/* {globalData?.totalqty} */}
                        </td>
                        {/* 
                                          <td
                                            className="force-style"
                                            style={{
                                              textAlign: "center",
                                              fontSize: "12px",
                                              fontWeight: 600,
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                                            }}
                                          >
                                            {globalData?.totalsoldprice}
                                          </td> */}
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            fontWeight: 600,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        >
                          {documentConfig?.totalBasePrice}
                        </td>
                        <td></td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            fontWeight: 600,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        >
                          {documentConfig?.totalTaxAmount}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            fontWeight: 600,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        >
                           {documentConfig?.grandTotal}
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "left",
                            fontSize: "12px",
                            fontWeight: 600,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                          colSpan="3"
                        >
                          Total Amount In Words
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            fontWeight: 500,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                          colSpan="4"
                        >
                          Total Amount Before Tax
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            fontWeight: 500,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        >
                          {documentConfig?.totalBasePrice}
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            fontWeight: 500,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                          colSpan="3"
                          rowSpan="5"
                        >
                          {documentConfig?.amountInWords}
                        </td>
                      </tr>
                      {documentConfig?.fromData?.statecode &&
                      documentConfig?.toData?.statecode &&
                      documentConfig?.fromData?.statecode !==
                        documentConfig?.toData?.statecode ? (
                        <tr>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              fontWeight: 500,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                            colSpan="4"
                          >
                            IGST
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              fontWeight: 500,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            {documentConfig?.igstAmount}
                          </td>
                        </tr>
                      ) : (
                        <>
                          <tr>
                            <td
                              className="force-style"
                              style={{
                                textAlign: "right",
                                fontSize: "12px",
                                fontWeight: 500,
                                "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                              colSpan="4"
                            >
                              SGST
                            </td>
                            <td
                              className="force-style"
                              style={{
                                textAlign: "right",
                                fontSize: "12px",
                                fontWeight: 500,
                                "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                            >
                              {documentConfig?.sgstAmount}
                            </td>
                          </tr>
                          <tr>
                            <td
                              className="force-style"
                              style={{
                                textAlign: "right",
                                fontSize: "12px",
                                fontWeight: 500,
                                "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                              colSpan="4"
                            >
                              CGST
                            </td>
                            <td
                              className="force-style"
                              style={{
                                textAlign: "right",
                                fontSize: "12px",
                                fontWeight: 500,
                                "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                            >
                              {documentConfig?.cgstAmount}
                            </td>
                          </tr>
                        </>
                      )}
                      <tr>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            fontWeight: 500,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                          colSpan="4"
                        >
                          TOTAL
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            fontWeight: 500,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        >
                          {documentConfig?.totalReturnAmount}
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "14px",
                            fontWeight: 600,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                          }}
                          colSpan="4"
                        >
                          Grand Total (Rounded)
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "14px",
                            fontWeight: 600,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        >
                          {documentConfig?.returnGrandTotalRounded}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {parseAmount(documentConfig?.totalDiscount) > 0 && (
                    <table width="100%">
                      <tbody>
                        <tr>
                          <td
                            style={{
                              fontSize: "13px",
                              fontWeight: 600,
                              textAlign: "center",
                              padding: "8px",
                              color: "black",
                            }}
                          >
                            Your Total Discount : 
                            {documentConfig?.totalDiscount}
                            {/* <br />
                                    <div className="mt-1 fs-20">
                                      ***** Thank You! Visit Us Again *****
                                    </div> */}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                  {/* {(payDetails?.CASH > 0 ||
                          payDetails?.CARD > 0 ||
                          payDetails?.ONLINE > 0 ||
                          payDetails?.UPI > 0 ||
                          payDetails?.PAYLATER > 0) && (
                          <table
                            className="items mt-2"
                            width="38%"
                            cellPadding="4"
                          >
                            <thead>
                              <tr>
                                <th
                                  colSpan={2}
                                  className="force-style"
                                  style={{
                                    width: "100%",
                                    textAlign: "center",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    "--th-bg":
                                      selectedTheme?.templateTheme
                                        ?.primaryColor,
                                    "--th-color":
                                      selectedTheme?.templateTheme
                                        ?.foregroundColor,
                                  }}
                                >
                                  Refund Method
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {payDetails?.CASH > 0 && (
                                <tr>
                                  <td
                                    className="force-style"
                                    style={{
                                      textAlign: "start",
                                      fontSize: "12px",
                                      width: "10%",
                                      "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    }}
                                  >
                                    Cash
                                  </td>
                                  <td
                                    className="force-style"
                                    style={{
                                      textAlign: "start",
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      width: "10%",
                                      "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    }}
                                  >
                                    
                                    {CurrencyConverter.toCurrency(
                                      payDetails?.CASH || 0,
                                      "INR",
                                    )}
                                  </td>
                                </tr>
                              )}
                              {payDetails?.CARD > 0 && (
                                <tr>
                                  <td
                                    className="force-style"
                                    style={{
                                      textAlign: "start",
                                      fontSize: "12px",
                                      width: "10%",
                                      "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    }}
                                  >
                                    Card
                                  </td>
                                  <td
                                    className="force-style"
                                    style={{
                                      textAlign: "start",
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      width: "10%",
                                      "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    }}
                                  >
                                    
                                    {CurrencyConverter.toCurrency(
                                      payDetails?.CARD || 0,
                                      "INR",
                                    )}
                                  </td>
                                </tr>
                              )}
                              {payDetails?.ONLINE > 0 && (
                                <tr>
                                  <td
                                    className="force-style"
                                    style={{
                                      textAlign: "start",
                                      fontSize: "12px",
                                      width: "10%",
                                      "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    }}
                                  >
                                    OnlineAmount
                                  </td>
                                  <td
                                    className="force-style"
                                    style={{
                                      textAlign: "start",
                                      fontSize: "12px",
                                      width: "10%",
                                      fontWeight: "600",
                                      "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    }}
                                  >
                                    
                                    {CurrencyConverter.toCurrency(
                                      payDetails?.ONLINE || 0,
                                      "INR",
                                    )}
                                  </td>
                                </tr>
                              )}
                              {payDetails?.UPI > 0 && (
                                <tr>
                                  <td
                                    className="force-style"
                                    style={{
                                      textAlign: "start",
                                      fontSize: "12px",
                                      width: "10%",
                                      "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    }}
                                  >
                                    UPI
                                  </td>
                                  <td
                                    className="force-style"
                                    style={{
                                      textAlign: "start",
                                      fontSize: "12px",
                                      width: "10%",
                                      fontWeight: "600",
                                      "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    }}
                                  >
                                    
                                    {CurrencyConverter.toCurrency(
                                      payDetails?.UPI || 0,
                                      "INR",
                                    )}
                                  </td>
                                </tr>
                              )}
                              {payDetails?.PAYLATER > 0 && (
                                <tr>
                                  <td
                                    className="force-style"
                                    style={{
                                      textAlign: "start",
                                      fontSize: "12px",
                                      width: "10%",
                                      "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    }}
                                  >
                                    BalanceDue
                                  </td>
                                  <td
                                    className="force-style"
                                    style={{
                                      textAlign: "start",
                                      fontSize: "12px",
                                      width: "10%",
                                      fontWeight: "600",
                                      "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    }}
                                  >
                                    
                                    {CurrencyConverter.toCurrency(
                                      payDetails?.PAYLATER || 0,
                                      "INR",
                                    )}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        )} */}
                  {documentConfig?.remarks && (
                    <table width="100%" className="mb-2 mt-2">
                      <tbody>
                        <tr>
                          <td width="100%">
                            <div className="font-12 fw-600 align-left text-black mb-1">
                              Remark
                            </div>

                            <div
                              className="font-12 p-0"
                              style={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                minHeight: "30px",
                              }}
                            >
                              {capitalizeFirstLetter(documentConfig?.remarks)}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}

                  <div className="mt-2 fs-20 d-flex justify-content-center fw-bold">
                    <span>***** Thank You! Visit Us Again *****</span>
                  </div>
                  {storeSettings?.negobillBranding && (
                    <>
                      <div className="mt-1 fs-20 d-flex justify-content-center fw-bold">
                        <span>Powered By</span>
                      </div>
                      <div className=" fs-20 d-flex justify-content-center fw-bold">
                        <span>www.NegoBill.com</span>
                      </div>
                    </>
                  )}
                  <table width="100%">
                    <tbody>
                      <tr>
                        <td
                          style={{
                            padding: "8px",
                            marginTop: "15px",
                          }}
                          className="d-flex align-items-start justify-content-end"
                        >
                          <div>
                            {storeSettings.invoiceSettings.signature && (
                              <div className="text-end p-0 fw-bold ">
                                <img
                                  src={
                                    uRLs.IMAGE_URL +
                                    storeSettings.invoiceSettings.signature
                                  }
                                  width={150}
                                />
                              </div>
                            )}
                            <div className="text-end p-0">
                              Issued By,Signature
                            </div>
                            <div className="text-end p-0 fw-bold ">
                              {documentConfig?.fromData?.title}
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ReturnSaleInvoice;
